/**
 * Pinia / shell helper around `dispatch`: apply the new model into a holder
 * (`shallowRef`) synchronously, then run Cmds. Elm order, same as `useTea`.
 *
 * `dispatch` alone only returns the next model after Cmds finish, which is
 * wrong for stores whose callers read state right after a sync mutator.
 * Msgs whose `update` returns no Cmds therefore take effect immediately.
 */
import { dispatch, type RunCmd, type UpdateFn } from "./dispatch";

/** Anything with a replaceable `.value` (a `ShallowRef`, in practice). */
export type ModelHolder<Model> = { value: Model };

export async function dispatchInto<Model, Msg, Cmd>(
  holder: ModelHolder<Model>,
  msg: Msg,
  update: UpdateFn<Model, Msg, Cmd>,
  runCmd: RunCmd<Cmd>,
): Promise<void> {
  await dispatch(
    holder.value,
    msg,
    (model, nextMsg) => {
      const result = update(model, nextMsg);
      holder.value = result[0];
      return result;
    },
    runCmd,
  );
}
